/**
 * countryTimezone.ts — map a country to its IANA timezone and compute the
 * hour offset between the band's home zone and a venue zone, so the day sheet
 * can show e.g. "CET · +1h vs CH". (2026-08-12)
 */

const COUNTRY_TO_TZ: Record<string, string> = {
  // Europe
  Switzerland: 'Europe/Zurich',
  France: 'Europe/Paris',
  Germany: 'Europe/Berlin',
  Belgium: 'Europe/Brussels',
  Netherlands: 'Europe/Amsterdam',
  Luxembourg: 'Europe/Luxembourg',
  Austria: 'Europe/Vienna',
  Spain: 'Europe/Madrid',
  Italy: 'Europe/Rome',
  Portugal: 'Europe/Lisbon',
  Ireland: 'Europe/Dublin',
  Finland: 'Europe/Helsinki',
  Greece: 'Europe/Athens',
  'Czech Republic': 'Europe/Prague',
  Czechia: 'Europe/Prague',
  Česko: 'Europe/Prague',
  'United Kingdom': 'Europe/London',
  UK: 'Europe/London',
  'Great Britain': 'Europe/London',
  Poland: 'Europe/Warsaw',
  Sweden: 'Europe/Stockholm',
  Norway: 'Europe/Oslo',
  Denmark: 'Europe/Copenhagen',
  Hungary: 'Europe/Budapest',
  Romania: 'Europe/Bucharest',
  Bulgaria: 'Europe/Sofia',
  Croatia: 'Europe/Zagreb',
  Serbia: 'Europe/Belgrade',
  Ukraine: 'Europe/Kiev',
  Turkey: 'Europe/Istanbul',
  // North America
  'United States': 'America/New_York',
  USA: 'America/New_York',
  'United States of America': 'America/New_York',
  Canada: 'America/Toronto',
  Mexico: 'America/Mexico_City',
  // Asia
  Japan: 'Asia/Tokyo',
  China: 'Asia/Shanghai',
  Kazakhstan: 'Asia/Almaty',
  India: 'Asia/Kolkata',
  'South Korea': 'Asia/Seoul',
  Korea: 'Asia/Seoul',
  Thailand: 'Asia/Bangkok',
  Singapore: 'Asia/Singapore',
  // Middle East
  'United Arab Emirates': 'Asia/Dubai',
  UAE: 'Asia/Dubai',
  Israel: 'Asia/Jerusalem',
  // Oceania
  Australia: 'Australia/Sydney',
  'New Zealand': 'Pacific/Auckland',
};

/** Normalize a country string (lowercase, trim, strip accents). */
function norm(s: string): string {
  return (s || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z]/g, '');
}

const NORM_TZ: Record<string, string> = {};
for (const [country, tz] of Object.entries(COUNTRY_TO_TZ)) NORM_TZ[norm(country)] = tz;

/** Resolve the IANA timezone for a country name (null when unknown). */
export function countryToTimezone(country?: string | null): string | null {
  if (!country) return null;
  return NORM_TZ[norm(country)] ?? null;
}

/**
 * Current UTC offset (hours, incl. DST) of an IANA timezone. Returns null when
 * the zone is unknown/invalid. Computed via Intl (no dependency, DST-aware).
 */
export function zoneUtcOffsetHours(tz: string | null | undefined): number | null {
  if (!tz) return null;
  try {
    const fmt = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      hour12: false,
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    });
    // "en-US" 12-hour clock quirk: force "24" style by grabbing parts ourselves.
    const parts = fmt.formatToParts(new Date());
    const get = (t: string) => parts.find((p) => p.type === t)?.value || '';
    const y = Number(get('year')), mo = Number(get('month')), d = Number(get('day'));
    const h = Number(get('hour')), mi = Number(get('minute')), s = Number(get('second'));
    if (Number.isNaN(y) || Number.isNaN(mo) || Number.isNaN(d)) return null;
    // Offset in hours = (zoned time as if UTC) - (actual UTC now), normalized.
    const now = new Date();
    const nowUTC = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(),
      now.getUTCHours(), now.getUTCMinutes(), now.getUTCSeconds());
    // Reconstruct what the zoned instant would be in the same "now" frame.
    // Simpler: use the local components of the zoned date as if UTC, compare.
    const candidate = Date.UTC(y, mo - 1, d, h, mi, s);
    // The zoned clock time corresponds to some UTC instant; offset = clock - utc.
    // We derive it by finding the difference between the zoned wall-clock-as-UTC
    // and the real UTC now, both for the current second.
    const diffMin = Math.round((candidate - nowUTC) / 60000);
    // Offset is candidate-as-UTC minus nowUTC? No — offset = tzWallClock − utcNow.
    // For an instant now, if zone is +2, zoned clock = utcNow + 2h. So:
    // candidateAsUTC (= zoned clock encoded as UTC) − nowUTC = +2h. ✓
    const hours = Math.round(diffMin / 60);
    return hours;
  } catch {
    return null;
  }
}

/** Signed hour difference (venueTz − bandTz), e.g. +1. Null when unknown. */
export function timezoneOffsetHours(
  bandTimezone: string | null | undefined,
  venueTimezone: string | null | undefined,
): number | null {
  const b = zoneUtcOffsetHours(bandTimezone);
  const v = zoneUtcOffsetHours(venueTimezone);
  if (b == null || v == null) return null;
  return v - b;
}

/** Human label, e.g. "CET · +1h vs home" or "+1h". */
export function timezoneOffsetLabel(
  bandTimezone: string | null | undefined,
  venueTimezone: string | null | undefined,
): string {
  const d = timezoneOffsetHours(bandTimezone, venueTimezone);
  if (d == null) return '';
  if (d === 0) return 'same time';
  return `${d > 0 ? '+' : ''}${d}h vs home`;
}
